import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { useSettings } from "@/contexts/SettingsContext";

const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

export const Hero = () => {
  const { settings, waLink } = useSettings();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "12%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section
      id="hero"
      ref={ref}
      className="relative overflow-hidden bg-cream min-h-[calc(100vh-96px)] pt-10 md:pt-14 pb-16 md:pb-0 flex items-center"
    >
      {/* Two-tone split background */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute inset-y-0 right-0 w-full md:w-[58%] bg-honey-light/70" />
        <div className="absolute right-[6%] top-1/2 h-[420px] w-[420px] md:h-[600px] md:w-[600px] -translate-y-1/2 rounded-full bg-honey/30 blur-3xl" />
      </div>

      <div className="relative mx-auto max-w-7xl px-6 md:px-10 grid grid-cols-1 md:grid-cols-2 gap-14 items-center w-full">
        {/* Left: copy */}
        <motion.div style={{ opacity: fade }} className="relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.7 }}
            className="mb-5 flex items-center gap-2 text-xs md:text-sm uppercase tracking-[0.25em] font-bold text-honey-dark"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-honey" />
            {settings.location_text}
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="font-display text-ink leading-[1.05] tracking-tight text-4xl sm:text-5xl lg:text-6xl font-medium"
          >
            Madu Murni,
            <br />
            Diambil Apa Adanya.
            <br />
            Pilih <span className="text-honey-dark">{settings.brand_name}</span>.
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.55, duration: 0.8 }}
            className="mt-6 max-w-md text-base md:text-lg text-stone2 leading-relaxed"
          >
            Dipanen langsung dari peternak lebah lokal Jombang. Tanpa campuran,
            tanpa pengawet — hanya kebaikan alam dalam setiap tetes.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.75, duration: 0.8 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <a
              data-testid="hero-whatsapp-btn"
              href={waLink("Halo Samsi Madu, saya tertarik dengan produk madu Anda.")}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 rounded-full bg-ink px-7 py-3.5 text-base font-semibold text-cream hover:bg-brandRed transition-colors duration-300"
            >
              Pesan Sekarang
            </a>
            <button
              data-testid="hero-produk-btn"
              onClick={() => scrollTo("produk")}
              className="inline-flex items-center gap-2 rounded-full border border-ink/25 px-7 py-3.5 text-base font-semibold text-ink hover:border-honey-dark hover:text-honey-dark transition-colors duration-300"
            >
              Lihat Produk
            </button>
          </motion.div>
        </motion.div>

        {/* Right: product shot */}
        <motion.div
          initial={{ opacity: 0, scale: 0.92 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.4, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          style={{ y: imgY }}
          className="relative z-10 flex justify-center md:justify-end md:-mr-6 lg:-mr-12"
        >
          <img
            src="/madu.png"
            alt="Madu Samsi Madu — botol madu murni, sarang lebah, dan madu segar"
            className="w-full max-w-md md:max-w-2xl drop-shadow-[0_40px_60px_rgba(28,25,23,0.25)]"
          />
        </motion.div>
      </div>
    </section>
  );
};
